import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { PublicDayOthersSection } from "../components/PublicDayOthersSection";
import { PublicEventShareRow } from "../components/PublicEventShareRow";
import { useVisitorEventRequestModals } from "../hooks/useVisitorEventRequestModals";
import type { DayBundle, PublicEvent } from "../types/public";
import { formatDoorCurtainSlots, publicPerformersList, remoteKindLabel, weekdayJp } from "../types/public";
import { sortByDateTimeThenVenue } from "../lib/event-sort";
import { normalizeDayBundle } from "../lib/normalize-public-bundle";
import { nearbyPublicEvents } from "../lib/nearby-public-events";
import { getVisitorSubmitterHash, getVisitorSubmitterKey } from "../lib/visitor-submitter";

function openTimeLine(ev: PublicEvent): string {
  return String(ev.curtain_time ?? "").trim() || String(ev.time_start ?? "").trim() || "—";
}

function KarinDetail({ e, pool }: { e: PublicEvent; pool: PublicEvent[] }) {
  const perfLine = publicPerformersList(e);
  const near = useMemo(() => nearbyPublicEvents(e, pool), [e, pool]);
  return (
    <article className="event karin karin-hero" style={{ marginTop: 10 }}>
      <div className="sparkle karin-hero-title">
        <span className="karin-sparkle-star">✦</span>
        <h3 className="title">{e.event_name}</h3>
      </div>
      <div className="meta">
        {e.event_date}（{weekdayJp(e.event_date)}）・{e.prefecture ?? ""} {e.venue ?? "会場未定"}
        {remoteKindLabel(e.event_kind) ? <>・{remoteKindLabel(e.event_kind)}</> : null}
      </div>
      <div className="meta">
        開始 {e.time_start ?? "—"} / 終了 {e.time_end ?? "—"}
      </div>
      {(() => {
        const slots = formatDoorCurtainSlots(e);
        const perf = e.performance_text?.trim();
        if (slots) {
          return (
            <>
              <div className="meta">{slots}</div>
              {perf ? (
                <div className="meta" style={{ whiteSpace: "pre-wrap", fontSize: 12, lineHeight: 1.5 }}>
                  {perf}
                </div>
              ) : null}
            </>
          );
        }
        if (perf) {
          return (
            <div className="meta" style={{ whiteSpace: "pre-wrap", fontSize: 12, lineHeight: 1.5 }}>
              {perf}
            </div>
          );
        }
        return (
          <div className="meta">
            開場 {e.door_time ?? "—"} / 開演 {e.curtain_time ?? "—"}
          </div>
        );
      })()}
      <div className="meta">出演者：{perfLine.length ? perfLine.join(" / ") : "—"}</div>
      {e.description ? <div className="meta">{e.description}</div> : null}
      {e.memo ? <div className="meta">メモ：{e.memo}</div> : null}
      <div className="row" style={{ marginTop: 10 }}>
        {e.detail_url ? (
          <a className="btn small secondary" href={e.detail_url} target="_blank" rel="noreferrer">
            詳しくはこちら🫶
          </a>
        ) : (
          <span className="pill" style={{ fontSize: 12 }}>
            これから公開だよ！
          </span>
        )}
        {e.reserve_url ? (
          <a className="btn small" href={e.reserve_url} target="_blank" rel="noreferrer">
            申し込みはこちら🫶
          </a>
        ) : null}
      </div>
      <div className="notice">申込み・詳細は必ずリンク先の公式情報をご確認ください。</div>
      <PublicEventShareRow event={e} />
      {near.length ? (
        <div className="home-nearby-block">
          <h4 className="home-nearby-title">近くで開催中</h4>
          <ul className="home-nearby-list">
            {near.map((x) => (
              <li key={x.id} className="home-nearby-li">
                <div className="home-nearby-meta">
                  <div>
                    <strong>出演</strong> {publicPerformersList(x).join("・") || "—"}
                  </div>
                  <div>
                    <strong>{x.event_name}</strong>
                  </div>
                  <div>
                    開演 {openTimeLine(x)} ・ {x.prefecture ? `${x.prefecture} ` : ""}
                    {x.venue ?? "会場未定"}
                  </div>
                </div>
                {x.detail_url ? (
                  <a className="btn small secondary home-nearby-link-btn" href={String(x.detail_url)} target="_blank" rel="noreferrer" title="公式詳細">
                    🔗
                  </a>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </article>
  );
}

export function DayPage() {
  const { date } = useParams();
  const [bundle, setBundle] = useState<DayBundle | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [visitorSubmitterHash, setVisitorSubmitterHash] = useState<string | null>(null);
  const { openCorrectionModal, visitorModalEl } = useVisitorEventRequestModals();

  useEffect(() => {
    let alive = true;
    void getVisitorSubmitterHash().then((hash) => {
      if (alive) setVisitorSubmitterHash(hash);
    });
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    if (!date) return;
    let alive = true;
    (async () => {
      try {
        const res = await fetch(`/public-data/day/${date}.json`, { cache: "no-store" });
        if (!res.ok) throw new Error(`読み込みに失敗しました (${res.status})`);
        const raw = await res.json();
        const data = normalizeDayBundle(raw);
        if (!data) throw new Error("日データの形式が不正です");
        if (alive) setBundle(data);
      } catch (e) {
        if (alive) setErr(String(e));
      }
    })();
    return () => {
      alive = false;
    };
  }, [date]);

  const karinSorted = useMemo(
    () => sortByDateTimeThenVenue((bundle?.events ?? []).filter((e) => e.is_karin) as PublicEvent[]),
    [bundle],
  );
  const others = useMemo(
    () => (bundle?.events ?? []).filter((e) => !e.is_karin) as PublicEvent[],
    [bundle],
  );

  const canDeleteFanPost = (e: PublicEvent) =>
    e.source === "fan_post" && Boolean(visitorSubmitterHash && e.fan_submitter_key_hash && e.fan_submitter_key_hash === visitorSubmitterHash);

  const deleteOwnFanPost = async (e: PublicEvent) => {
    if (!canDeleteFanPost(e)) return;
    if (!window.confirm("この投稿を削除しますか？投稿した端末からだけ削除できます。")) return;
    try {
      const res = await fetch(`/api/public/events/${encodeURIComponent(e.id)}`, {
        method: "DELETE",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ submitterKey: getVisitorSubmitterKey() }),
      });
      const j = (await res.json().catch(() => ({}))) as { error?: string };
      if (!res.ok) throw new Error(j.error ?? `HTTP ${res.status}`);
      setBundle((prev) => (prev ? { ...prev, events: prev.events.filter((x) => x.id !== e.id) } : prev));
      setErr("投稿を削除しました。カレンダー反映に少し時間がかかることがあります。");
    } catch (error) {
      setErr(`削除できませんでした: ${String(error)}`);
    }
  };

  return (
    <>
      <div className="shell">
      <header className="topbar">
        <div className="brand">
          <h1>日別詳細</h1>
          <p className="mono">{date}</p>
        </div>
        <Link className="pill" to="/">
          もどる
        </Link>
      </header>

      {err && <p style={{ color: "#b00020" }}>{err}</p>}

      <section className="card">
        <h2 style={{ margin: "0 0 8px", fontSize: 15 }}>宮本佳林（開演が早い順）</h2>
        {karinSorted.length === 0 ? (
          <div className="meta">この日の予定はまだないみたいです</div>
        ) : (
          karinSorted.map((e) => <KarinDetail key={e.id} e={e} pool={bundle?.events ?? []} />)
        )}
      </section>

      {others.length === 0 ? (
        <section className="card" style={{ marginTop: 12 }}>
          <h2 style={{ margin: "0 0 8px", fontSize: 15 }}>他グループ（開演が早い順・かぶり確認用）</h2>
          <div className="meta">他グループの予定はありません</div>
        </section>
      ) : (
        <PublicDayOthersSection
          events={others}
          onCorrectionRequest={openCorrectionModal}
          canDeleteEvent={canDeleteFanPost}
          onDeleteRequest={(e) => void deleteOwnFanPost(e)}
        />
      )}
    </div>
    {visitorModalEl}
    </>
  );
}
